import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Heading2 } from '../ui/Typography/Heading';
import { Button } from '../ui/Button/Button';
import { Modal, Tooltip } from '../ui';
import { IconInfo, IconReset, IconCircledCheck, IconCheck } from '../ui/Icons';
import { clearImportFile, executeImport } from '../../redux/import/slice';
import {
  selectCheckImportFileLoading,
  selectCheckImportFileFailed,
  selectImportJsonData,
  selectImportFile,
  selectExecuteImportLoading,
  selectExecuteImportSucceeded
} from '../../redux/import/selectors';
import { useWindowSize } from '../../hooks';
import { IMPORT_TABLE_ROW_STATUSES } from '../../consts/consts';
import {
  ImportSuccessDropzone,
  ImportSuccessText,
  ImportSuccessSubText,
  ImportSuccessSelectLink,
  ImportSuccessIconWrapper,
  ImportUploaderVisibilityWrapper,
  ImportModalFooterActions,
  ImportModalFooterPlaceholder,
  ImportModalTitle,
  ImportModalBody,
  ImportModalLoadingState,
  ImportModalTableWrapper,
  ImportTableStatusTag,
  ImportTableStatusCircle,
  ImportTableStatusLabel,
  ImportTableErrorCellWrapper,
  ImportTableErrorCellText,
  ImportTableErrorCellInfoIconWrapper,
  ImportTableErrorTooltipMessageList,
  ImportTableErrorTooltipMessage
} from '../ui/Layout/ImportLayout';
import { ImportUploaderSection } from './ImportUploaderSection';
import { ImportTableSection } from './ImportTableSection';
import { ImportLoadingState } from './ImportLoadingState';

const IMPORT_MODAL_VERTICAL_GAP = 60;
const IMPORT_MODAL_HEADER_HEIGHT = 240;
const IMPORT_MODAL_FOOTER_HEIGHT = 91;
const IMPORT_COLUMN_CHECKBOXES_HEIGHT = 45;
const IMPORT_TABLE_HEADER_HEIGHT = 41;
const IMPORT_TABLE_MIN_BODY_HEIGHT = 160;
const DEFAULT_HELP_CENTER_URL =
  'https://help.oqio.de/de/articles/13611129-umgang-mit-fehlern-beim-stammdaten-import?utm_source=import&utm_medium=inproduct';

const renderErrorMessageTooltip = (errorMessages) => {
  return (
    <ImportTableErrorTooltipMessageList>
      {errorMessages.map((message) => (
        <ImportTableErrorTooltipMessage key={message}>{message}</ImportTableErrorTooltipMessage>
      ))}
    </ImportTableErrorTooltipMessageList>
  );
};

const renderErrorMessageCell = (errorMessages) => {
  if (!errorMessages) {
    return <React.Fragment />;
  }

  const errorMessageCount = errorMessages.length;
  const errorCellText = `${errorMessageCount} Fehler`;

  return (
    <ImportTableErrorCellWrapper>
      <ImportTableErrorCellText>{errorCellText}</ImportTableErrorCellText>
      <Tooltip title={renderErrorMessageTooltip(errorMessages)} width={300}>
        <ImportTableErrorCellInfoIconWrapper>
          <IconInfo />
        </ImportTableErrorCellInfoIconWrapper>
      </Tooltip>
    </ImportTableErrorCellWrapper>
  );
};

const renderStatusCell = (status) => {
  return (
    <ImportTableStatusTag $color={status.color}>
      <ImportTableStatusCircle $color={status.color} />
      <ImportTableStatusLabel>{status.label}</ImportTableStatusLabel>
    </ImportTableStatusTag>
  );
};

const getDataRowStatus = (row) => {
  const { valid_row, new_policy, new_risk, new_policy_to_risk, updated_risk, updated_policy } = row;
  if (!valid_row) {
    return IMPORT_TABLE_ROW_STATUSES.invalid;
  }

  if (updated_risk || updated_policy) {
    return IMPORT_TABLE_ROW_STATUSES.updated;
  }

  if (new_policy || new_risk || new_policy_to_risk) {
    return IMPORT_TABLE_ROW_STATUSES.new;
  }

  return IMPORT_TABLE_ROW_STATUSES.no_changes;
};

export const ImportFlow = ({
  onSuccess,
  hasError = false,
  initialShowSuccess = false,
  helpCenterUrl = DEFAULT_HELP_CENTER_URL
}) => {
  const dispatch = useDispatch();
  const checkImportFileLoading = useSelector(selectCheckImportFileLoading);
  const checkImportFileFailed = useSelector(selectCheckImportFileFailed);
  const importJsonData = useSelector(selectImportJsonData);
  const importFile = useSelector(selectImportFile);
  const executeImportLoading = useSelector(selectExecuteImportLoading);
  const executeImportSucceeded = useSelector(selectExecuteImportSucceeded);

  const [shouldOpenFileDialog, setShouldOpenFileDialog] = useState(false);
  const [showSuccess, setShowSuccess] = useState(initialShowSuccess);
  const [importTableColumns, setImportTableColumns] = useState([]);
  const [importTableData, setImportTableData] = useState([]);
  const { height: windowHeight } = useWindowSize();

  const hasModalFooter = !checkImportFileLoading && importJsonData && !executeImportLoading;
  const importTableBodyHeight = Math.max(
    (windowHeight || 0) -
      IMPORT_MODAL_VERTICAL_GAP -
      IMPORT_MODAL_HEADER_HEIGHT -
      IMPORT_MODAL_FOOTER_HEIGHT -
      IMPORT_COLUMN_CHECKBOXES_HEIGHT -
      IMPORT_TABLE_HEADER_HEIGHT,
    IMPORT_TABLE_MIN_BODY_HEIGHT
  );

  const parseImportData = (rawData) => {
    const columnKeysArray = Object.keys(rawData.header_row);
    const columns = columnKeysArray.map((key) => {
      const columnClassName = rawData.header_row[key] ? 'valid-column' : 'invalid-column';
      return {
        key,
        className: columnClassName,
        title: key,
        align: 'left',
        dataIndex: key,
        ellipsis: { showTitle: true },
        width: 200
      };
    });

    if (rawData.data_rows.length > 0) {
      columns.unshift({
        key: 'ROW',
        title: 'ZEILE',
        align: 'center',
        fixed: true,
        className: 'row-column',
        dataIndex: 'row',
        width: 80
      });
    }

    const allErrorMessages = rawData.data_rows
      .map((row) => row.error_messages)
      .filter((messagesArray) => messagesArray !== null)
      .flat();
    if (allErrorMessages.length > 0) {
      const uniqueErrorMessages = [...new Set(allErrorMessages)];
      const errorMessagesFilterOptions = uniqueErrorMessages.map((message) => {
        return { text: message, value: message };
      });
      columns.unshift({
        key: 'ERROR_MESSAGES',
        title: 'FEHLER',
        align: 'center',
        fixed: true,
        filters: errorMessagesFilterOptions,
        onFilter: (value, record) => record.error_messages?.includes(value),
        className: 'error-column',
        dataIndex: 'error_messages',
        width: 120,
        render: (errorMessages) => renderErrorMessageCell(errorMessages)
      });
    }

    const statusFilterOptions = Object.values(IMPORT_TABLE_ROW_STATUSES).map((status) => {
      return { text: status.label, value: status.label };
    });
    if (rawData.data_rows.length > 0) {
      columns.unshift({
        key: 'STATUS',
        title: 'STATUS',
        align: 'center',
        fixed: true,
        filters: statusFilterOptions,
        onFilter: (value, record) => record.status.label === value,
        className: 'status-column',
        dataIndex: 'status',
        width: 120,
        render: (status) => renderStatusCell(status)
      });
    }
    setImportTableColumns(columns);

    const parsedDataRows = rawData.data_rows.map((row, index) => {
      return {
        key: row.id || `row-${index}`,
        ...row,
        status: getDataRowStatus(row)
      };
    });
    setImportTableData(parsedDataRows);
  };

  useEffect(() => {
    if (importJsonData) {
      parseImportData(importJsonData);
    } else {
      setImportTableColumns([]);
      setImportTableData([]);
    }
  }, [importJsonData]);

  useEffect(() => {
    setShowSuccess(initialShowSuccess);
  }, [initialShowSuccess]);

  useEffect(() => {
    if (checkImportFileFailed) {
      dispatch(clearImportFile());
    }
  }, [checkImportFileFailed, dispatch]);

  const handleModalClose = () => {
    dispatch(clearImportFile());
  };

  const handleStartNewImport = () => {
    dispatch(clearImportFile());
    setShowSuccess(false);
    setShouldOpenFileDialog(true);
  };

  const handleExecuteImport = () => {
    dispatch(executeImport({ file: importFile }));
  };

  const handleNewUpload = () => {
    dispatch(clearImportFile());
    setShouldOpenFileDialog(true);
  };

  const handleImportSuccess = () => {
    dispatch(clearImportFile());
    setShowSuccess(true);
    onSuccess?.();
  };

  const renderModalFooter = () => {
    if (executeImportSucceeded) {
      return (
        <ImportModalFooterActions>
          <Button type="primary" onClick={handleImportSuccess}>
            <IconCircledCheck /> Fertig
          </Button>
        </ImportModalFooterActions>
      );
    }

    return (
      <ImportModalFooterActions>
        <Button type="text" onClick={handleModalClose}>
          Abbrechen
        </Button>
        <Button type="secondary" onClick={handleNewUpload}>
          <IconReset /> Neuer Upload
        </Button>
        <Button type="primary" onClick={handleExecuteImport} loading={executeImportLoading}>
          <IconCircledCheck /> Importieren
        </Button>
      </ImportModalFooterActions>
    );
  };

  const renderModalFooterPlaceholder = () => {
    return <ImportModalFooterPlaceholder />;
  };

  const modalTitle = (
    <ImportModalTitle>
      <Heading2>Ihre Stammdaten</Heading2>
      <p>
        Überprüfen Sie Ihre hochgeladenen Stammdaten und importieren Sie diese über den Button, um die Daten in oqio zu
        übernehmen. <br /> Wenn Fehlermeldungen aufkommen, lesen Sie die Hilfestellungen im Help Center, korrigieren Sie
        die Angaben in Ihrer Excel Tabelle und laden diese erneut hoch.
      </p>
      <a href={helpCenterUrl} target="_blank" rel="noopener noreferrer">
        Zu Hilfestellungen bei Fehlermeldungen
      </a>
    </ImportModalTitle>
  );

  return (
    <>
      {showSuccess && (
        <ImportSuccessDropzone>
          <ImportSuccessText>
            <ImportSuccessIconWrapper>
              <IconCheck />
            </ImportSuccessIconWrapper>
            Stammdaten erfolgreich importiert
          </ImportSuccessText>
          <ImportSuccessSubText>
            Für den Import anderer Stammdaten{' '}
            <ImportSuccessSelectLink type="button" onClick={handleStartNewImport}>
              Datei auswählen
            </ImportSuccessSelectLink>
          </ImportSuccessSubText>
        </ImportSuccessDropzone>
      )}
      <ImportUploaderVisibilityWrapper $hidden={showSuccess}>
        <ImportUploaderSection
          shouldOpenFileDialog={shouldOpenFileDialog}
          onFileDialogOpened={() => setShouldOpenFileDialog(false)}
          hasError={hasError}
        />
      </ImportUploaderVisibilityWrapper>

      <Modal
        className="import-preview-modal"
        open={importJsonData && !checkImportFileLoading && !checkImportFileFailed}
        mask={{ closable: false }}
        onCancel={handleModalClose}
        title={modalTitle}
        footer={hasModalFooter ? renderModalFooter() : renderModalFooterPlaceholder()}
        width="calc(100vw - 60px)"
      >
        <ImportModalBody>
          {executeImportLoading && (
            <ImportModalLoadingState>
              <ImportLoadingState
                className="import-loading-state-content"
                loadingIndicatorClassName="import-loading-indicator"
                isLoading={true}
                isFileCheckLoadingState={false}
                importSummary={importJsonData?.meta_data}
              />
            </ImportModalLoadingState>
          )}

          {executeImportSucceeded && !executeImportLoading && importJsonData && (
            <ImportLoadingState
              isLoading={false}
              isFileCheckLoadingState={false}
              importSummary={importJsonData?.meta_data}
            />
          )}

          {!checkImportFileLoading && !executeImportLoading && !executeImportSucceeded && importJsonData && (
            <ImportModalTableWrapper>
              <ImportTableSection
                className="import-table-section"
                importTableColumns={importTableColumns}
                importTableData={importTableData}
                customTableBodyHeight={importTableBodyHeight}
              />
            </ImportModalTableWrapper>
          )}
        </ImportModalBody>
      </Modal>

      <Modal
        className="import-file-check-modal"
        open={checkImportFileLoading}
        closable={false}
        mask={{ closable: false }}
        footer={null}
        width={585}
        centered
      >
        <ImportLoadingState isLoading={true} isFileCheckLoadingState={true} />
      </Modal>
    </>
  );
};
